import type { Envelope, MafiaEvent, MafiaState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal } from "../net/signal";
import { Pace } from "../net/pace";
import { Sequencer } from "../scene/queue";
import { ms } from "../game/speed";
import { synth } from "../game/sfx";
import { fa } from "../game/cards";
import { botText } from "../game/seats";
import {
  Ask, Ev, NOON_TITLE, Note, Say, StepKind, Team, dayTitle, eventText, nightTitle, noteText, roleTeam, shownInLog, speechText, wakeText,
  type MfEvent, type MfNote,
} from "./text";

/** یک خطِ دفترچه. secret = یادداشتِ خصوصیِ نقش. */
export type LogLine = { text: string; secret: boolean; day: number };

const wait = (t: number) => new Promise<void>((r) => setTimeout(r, ms(t)));

/**
 * جلوه‌ی گذرای میز — همتای MafiaView.Cine ِ اپ: عنوانِ شب و روز، تیرِ شب، مهرِ «بیرون»/«دفاع»، هاله‌ی
 * برگشت، نورافکنِ افشا، نشانه‌ی کارِ شبِ من، سپرِ «کسی نرفت»، خطِ اتهام و تفنگ، جشنِ پایان.
 * seat و to صندلی‌اند (‎-1 = وسطِ میز).
 */
export type FxKind = "title" | "shot" | "out" | "defense" | "halo" | "spot" | "target" | "shield" | "line" | "gun" | "finale" | "tally" | "react";

/** واکنش‌ها به ترتیبِ کد (MafiaAction kind ۷، say) — نشانه‌ها: tools/mafia_react_art.py ← /art/mafia/react/. */
export const REACTIONS = [
  { key: "like", fa: "لایک" }, { key: "dislike", fa: "دیس‌لایک" }, { key: "laugh", fa: "خنده" }, { key: "angry", fa: "عصبانی" },
  { key: "shock", fa: "شوکه" }, { key: "think", fa: "فکر" }, { key: "eyes", fa: "نگاه چپ" }, { key: "cool", fa: "باحال" },
] as const;
export type Tone = "mafia" | "town" | "indep" | "gold";
/** list/n: کارتِ شمارشِ رأی — رأی‌دهنده‌ها (خالی = رأیِ مخفی) و شمار؛ text = نوعِ رأی (۱ اول، ۲ خروج، ۳ بازجویی). */
export type Fx = { kind: FxKind; key: number; seat: number; to: number; tone: Tone; text: string; night: boolean; list: number[]; n: number };
export const toneOf = (team: number): Tone => (team === Team.Mafia ? "mafia" : team === Team.Independent ? "indep" : "town");

/**
 * میزِ مافیای آنلاین روی وب — همتای MafiaOnlineController ِ اپ و MafiaTable ِ سرور
 * (Backend/internal/table/mafia.go).
 *
 * قاعده روی سرور است. نمای سرور (MafiaState) همان «مدل» است؛ جریان (MafiaEvent) سه رشته‌ی شماره‌دار
 * دارد — رویدادِ علنی، جمله، یادداشتِ خصوصی — و پیشنهادهای تیمِ مافیا. تکراری (بازپخشِ پس از اتصال)
 * کنار می‌رود، جاافتاده Resync می‌خواهد. داده همان دم در دفترچه می‌نشیند؛ فقط نمایش (اعلام، حباب، روز و
 * شب) در صفِ صحنه است تا وضعیتِ تازه زیرِ اعلامِ قبلی ننشیند.
 */
export class MafiaOnline {
  /** آخرین نمای اعمال‌شده. */
  readonly model = signal<MafiaState | null>(null);
  /** متنِ وسطِ میز: اعلامِ گرداننده یا عنوانِ روز و شب. */
  readonly stage = signal<{ text: string; key: number } | null>(null);
  /** حبابِ جمله روی صندلی‌ها: صندلی ← متن. */
  readonly bubbles = signal<Record<number, string>>({});
  readonly log = signal<LogLine[]>([]);
  /** خطِ خصوصیِ گذرا (یادداشتِ شب، پیشنهادِ هم‌تیمی). */
  readonly whisper = signal<{ text: string; key: number } | null>(null);
  /** پیشنهادهای هم‌تیمی‌ها در همین شب: صندلی ← هدف. */
  readonly suggestions = signal<Record<number, number>>({});
  readonly timer = signal<{ left: number; total: number } | null>(null);
  /** زودیاک: صاحبِ بمبِ امروز (−۱ = نیست) — از رویدادهای علنی. */
  readonly bomb = signal(-1);
  /** زودیاک: گروهِ اوشنی که من در آنم (همه شهروند) — از یادداشت‌های خودم. */
  readonly crew = signal<number[]>([]);
  /** کارتِ نقشِ آغاز را نشان بده. */
  readonly reveal = signal(false);
  /**
   * پخشِ کارت‌ها سرِ آغاز (MafiaState.revealing): "deal" کارت‌ها دورِ میز پخش می‌شوند، "card" کارتِ من بلند شده
   * و رو می‌شود (بقیه‌ی کارت‌ها روی میز می‌مانند)، "" تمام.
   */
  readonly deal = signal<"" | "deal" | "card">("");
  readonly ended = signal(false);
  /** جوابِ همین خواسته رفت (stepNo|ask) — تا نمای بعدی، پرسش بسته می‌ماند. */
  readonly sent = signal("");
  /** دستِ من در رأیِ علنیِ همین گام. */
  readonly hand = signal(false);

  // ---- سرعت‌گیرها — net/pace.ts؛ اعداد همان Backend/internal/table/mafia_pace.go ----
  /** واکنش‌ها: همان واکنش رو به همان کس ۴ ثانیه نمی‌رود؛ سه تا پشتِ هم، بعد هر ۲ ثانیه یکی. */
  readonly reactPace = new Pace(2000, 3, 4000);
  /** پیشنهادِ هدف به هم‌تیمی‌ها، شب. */
  private suggPace = new Pace(1500, 3, 4000);
  /** دو‌بار‌زدنِ دستِ رأی: بالا و بلافاصله پایین یعنی هیچ رأیی — لمسِ دوم در این فاصله کنار می‌رود. */
  private handPace = new Pace(0, 1, 400);
  /** جمله‌ی آخرِ من در همین گام — همان جمله دوباره (لمسِ دوباره) نمی‌رود و از سه جمله کم نمی‌کند. */
  private lastLine = "";
  /**
   * روز یا شب، همان لحظه که گرداننده اعلام می‌کند — با رویدادِ DayStart/NightStart و نه با نمای بعدی؛ وگرنه
   * شبِ کوتاه (معارفه) «روزِ اول» را زیرِ آسمانِ شب اعلام می‌کرد.
   */
  readonly phase = signal<{ night: boolean; day: number }>({ night: false, day: 0 });
  /** جلوه‌های در حالِ پخش (هر کدام خودش پاک می‌شود). */
  readonly fx = signal<Fx[]>([]);

  private seq = new Sequencer();
  private off: (() => void) | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;
  private evNext = 0;
  private spNext = 0;
  private noteNext = 0;
  private sgNext = 0;
  private resyncAsked = false;
  private deadlineAt = 0;
  private total = 0;
  private key = 0;
  private firstSeen = false;
  private suggestDay = -1;
  /** سرور پخشِ کارت دارد (revealing دیده شد)؛ پس اگر بی من شروع شد، کارت خودش بسته شود. */
  private sawReveal = false;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  /** نامزدهایی که در رأیِ اول به نصاب رسیدند (روز:صندلی) — مهرِ «دفاع» یک بار. */
  private reached = new Set<string>();
  private ending = false;
  /** رأی‌های بسته‌شده (رأی‌دهنده ← نامزد) تا شمارِ همان نامزد برسد. */
  private ballot: { voter: number; cand: number }[] = [];
  /** رویدادها و جمله‌ها برای نام‌ها — فقط نام از نما می‌آید. */
  private names: string[] = [];

  start(): void {
    this.off = client.on((env) => this.onMessage(env));
    const last = client.mafia.value;
    if (last) this.onState(last);
    this.tick = setInterval(() => this.updateTimer(), 200);
    if (typeof window !== "undefined") (window as unknown as { __mafia?: MafiaOnline }).__mafia = this;
  }

  dispose(): void {
    this.off?.(); this.off = null;
    this.seq.dispose();
    if (this.tick) clearInterval(this.tick);
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
  }

  seatName = (s: number): string => {
    const m = this.model.value;
    if (m && !m.watching && s === m.me) return "تو";
    return this.names[s] || `بازیکن ${s + 1}`;
  };

  // ─────────────────────────── پیام‌ها ───────────────────────────

  private onMessage(env: Envelope): void {
    const m = env.msg;
    if (m.case === "mafiaState") this.onState(m.value);
    else if (m.case === "mafiaEvent") this.onEvent(m.value);
  }

  private onState(st: MafiaState): void {
    this.names = st.seats.map((s) => botText(s.name, s.bot));   // متنِ اعلام‌ها: ربات با 🤖 — [[botText]]
    if (!this.firstSeen) {
      this.firstSeen = true;
      // کارتِ نقش فقط سرِ آغازِ بازی؛ برگشتِ وسطِ بازی نقش را روی چیپ می‌بیند. با revealing اول پخشِ کارت‌ها.
      if (!st.watching && (st.revealing || st.day === 0 && !st.night)) this.reveal.value = true;
      if (!st.watching && st.revealing) this.deal.value = "deal";
    }
    this.seq.enqueueSnapshot(() => this.apply(st));
  }

  private apply(st: MafiaState): void {
    const prev = this.model.value;
    if (!prev || prev.stepNo !== st.stepNo) {
      this.sent.value = "";
      this.hand.value = false;
      this.bubbles.value = {};
      // گامِ تازه: صدای گرداننده برای کسی که بیدار شد
      const noon = st.stepKind === StepKind.Noon;
      if ((st.night || noon) && st.prompt && !st.watching) this.say(wakeText(st.prompt.ask, st.mseats[st.me]?.role ?? 0));
      else if (st.night && st.teamLeader >= 0) this.say(wakeText(Ask.Team));
      else if (noon) this.say(NOON_TITLE + "…");
    }
    if (st.night && st.day !== this.suggestDay) { this.suggestDay = st.day; this.suggestions.value = {}; }
    if (!st.night && Object.keys(this.suggestions.value).length) this.suggestions.value = {};
    if (st.revealing) this.sawReveal = true;
    else if (this.sawReveal && this.reveal.value) {
      // مهلتِ پخش سر رسید و بازی بی «فهمیدم» ِ من شروع شد
      this.reveal.value = false;
      this.deal.value = "";
    }
    this.model.value = st;
    if (this.phase.value.night !== st.night || this.phase.value.day !== st.day) this.phase.value = { night: st.night, day: st.day };
    if (st.deadlineMs > 0 && !st.over) {
      const left = st.deadlineMs / 1000;
      if (!prev || prev.stepNo !== st.stepNo) this.total = left;
      this.total = Math.max(this.total, left);
      this.deadlineAt = performance.now() + st.deadlineMs;
    } else this.deadlineAt = 0;
    this.updateTimer();
    if (prev && !st.over && st.stepKind === StepKind.Vote1 && st.target >= 0 && st.threshold > 0) {
      const up = st.mseats.filter((s) => s.hand).length;
      const k = `${st.day}:${st.target}`;
      if (up >= st.threshold && !this.reached.has(k)) {
        this.reached.add(k);
        this.flashFx({ kind: "defense", seat: st.target, tone: "mafia" }, 1900);
        synth("stamp", { delay: 200 });
      }
    }
    if (st.over && !this.ended.value && !this.ending) {
      if (prev && !prev.over) {
        // پایان جلوی چشم: اول جشنِ تیمِ برنده، بعد برگه‌ی پایان
        this.ending = true;
        const w = st.winner;
        this.flashFx({ kind: "finale", tone: toneOf(w), text: w === Team.Mafia ? "مافیا برد" : w === Team.Independent ? "زودیاک برد" : "شهر برد" }, 2600);
        synth("fanfare");
        this.later(1800, () => { this.ended.value = true; });
      } else this.ended.value = true;
    }
  }

  // ─────────────────────────── جلوه ───────────────────────────

  private later(t: number, f: () => void): void {
    const id = setTimeout(() => { this.timers.delete(id); f(); }, ms(t));
    this.timers.add(id);
  }

  /** جلوه‌ای که پس از life میلی‌ثانیه (با سرعتِ بازی) خودش برمی‌دارد. */
  private flashFx(f: Partial<Fx> & { kind: FxKind }, life: number): void {
    const x: Fx = { seat: -1, to: -1, tone: "gold", text: "", night: false, list: [], n: 0, ...f, key: ++this.key };
    this.fx.value = [...this.fx.value, x];
    this.later(life, () => { this.fx.value = this.fx.value.filter((y) => y.key !== x.key); });
  }

  private seatOk(s: number): boolean { return s >= 0 && s < this.names.length; }

  /** جلوه‌ی رویدادِ علنی، هم‌زمان با اعلامِ گرداننده. */
  private eventFx(e: MfEvent): void {
    switch (e.kind) {
      case Ev.Died: case Ev.Terror: {
        const v = e.kind === Ev.Terror ? e.b : e.a;
        if (!this.seatOk(v)) return;
        this.flashFx({ kind: "shot", seat: v, tone: "mafia" }, 1600);
        this.flashFx({ kind: "out", seat: v, tone: "mafia" }, 2000);
        synth("bang"); synth("stamp", { delay: 300 });
        return;
      }
      case Ev.Exit:
        if (!this.seatOk(e.a)) return;
        this.flashFx({ kind: "out", seat: e.a, tone: "mafia" }, 2000);
        synth("stamp", { delay: 200 });
        return;
      case Ev.Revived: case Ev.Returned:
        if (!this.seatOk(e.a)) return;
        this.flashFx({ kind: "halo", seat: e.a, tone: "gold" }, 1600);
        synth("chime");
        return;
      case Ev.KaneReveal: case Ev.SideReveal: case Ev.RoleReveal: {
        if (!this.seatOk(e.a)) return;
        const team = e.kind === Ev.KaneReveal ? Team.Mafia : e.kind === Ev.SideReveal ? e.b : roleTeam(e.b);
        this.flashFx({ kind: "spot", seat: e.a, tone: toneOf(team) }, 2000);
        synth("whoosh", { volume: 0.6 });
        return;
      }
      case Ev.Gun:
        if (!this.seatOk(e.a) || !this.seatOk(e.b)) return;
        this.flashFx({ kind: "gun", seat: e.a, to: e.b, tone: "gold" }, 700);
        if (e.c === 1) { this.flashFx({ kind: "shot", seat: e.b, tone: "gold" }, 1600); synth("bang"); }
        else synth("tick");
        return;
      case Ev.NoDeath:
        this.flashFx({ kind: "shield", tone: "town" }, 1800);
        synth("chime");
        return;
      case Ev.Defendants:
        for (const d of e.list) if (this.seatOk(d)) this.flashFx({ kind: "spot", seat: d, tone: "mafia" }, 1600);
        return;
    }
  }

  /**
   * رأیِ یک نامزد بسته شد: در دفترچه چه کسی به چه کسی، و کارتِ شمارش وسطِ میز — نامِ رأی‌دهنده‌ها یکی‌یکی و
   * خطِ هر کدام تا نامزد. رأیِ مخفی (شب‌های مافیا) فقط شمار را می‌گوید.
   */
  private async tally(e: MfEvent): Promise<void> {
    const cand = e.a, n = e.b, kind = e.c;
    const voters = this.ballot.filter((b) => b.cand === cand).map((b) => b.voter);
    this.ballot = this.ballot.filter((b) => b.cand !== cand);
    const secret = voters.length < n;
    const who = secret ? "رأی مخفی" : voters.length ? voters.map(this.seatName).join("، ") : "هیچ‌کس";
    this.push({ text: `رأی برای ${this.seatName(cand)}: ${fa(n)} — ${who}`, secret: false, day: e.day });
    if (n <= 0 || !this.seatOk(cand)) return;
    const thr = this.model.value?.threshold ?? 0;
    const reached = kind === 1 && thr > 0 && n >= thr;
    const show = 500 + Math.min(voters.length, 12) * 110;
    const life = show + 1000;
    this.flashFx({ kind: "tally", seat: cand, list: secret ? [] : voters, n, tone: reached ? "mafia" : "gold", text: String(kind) }, life);
    if (!secret) for (const v of voters) if (this.seatOk(v) && v !== cand) this.flashFx({ kind: "line", seat: v, to: cand, tone: "gold" }, life);
    synth("tick");
    if (reached) synth("stamp", { delay: show });
    await wait(life);
  }

  /** هدف‌های کارِ شبِ من، همان دمِ «تأیید». */
  markTargets(seats: number[]): void {
    const m = this.model.value;
    if (!m || m.watching || !m.night) return;
    const tone = toneOf(roleTeam(m.mseats[m.me]?.role ?? 0));
    for (const s of seats) if (this.seatOk(s)) this.flashFx({ kind: "target", seat: s, tone }, 1100);
  }

  private updateTimer(): void {
    if (this.deadlineAt <= 0) { if (this.timer.value) this.timer.value = null; return; }
    const left = (this.deadlineAt - performance.now()) / 1000;
    this.timer.value = left > 0 ? { left, total: Math.max(left, this.total) } : null;
  }

  private onEvent(e: MafiaEvent): void {
    switch (e.kind) {
      case 1: {
        if (!this.inOrder("evNext", e.index)) return;
        const ev: MfEvent = { kind: e.code, day: e.day, night: e.night, a: e.a, b: e.b, c: e.c, list: [...e.list] };
        // زودیاک: نشانِ «بمب» روی صندلی تا خنثی/ترکیدن یا شبِ بعد (بازپخش هم)
        if (ev.kind === Ev.Bomb) this.bomb.value = ev.a;
        else if (ev.kind === Ev.BombResult || ev.kind === Ev.NightStart) this.bomb.value = -1;
        if (shownInLog(ev.kind)) {
          const t = eventText(ev, this.seatName);
          if (t) this.push({ text: t, secret: false, day: ev.day });
        }
        if (!e.replay) this.seq.enqueue(() => this.play(ev));
        return;
      }
      case 2: {
        if (!this.inOrder("noteNext", e.index)) return;
        const n: MfNote = { kind: e.code, day: e.day, a: e.a, b: e.b, role: e.role, list: [...e.list] };
        if (n.kind === Note.Ocean && n.b === 1) this.crew.value = [...n.list];
        const t = noteText(n, this.seatName);
        if (!t) return;
        this.push({ text: t, secret: true, day: n.day });
        if (!e.replay) this.seq.enqueue(() => this.whisperFor(t));
        return;
      }
      case 3: {
        if (!this.inOrder("spNext", e.index)) return;
        if (e.replay) return;
        const text = speechText(e.code, e.a, e.reason, this.seatName, e.index);
        const seat = e.seat, to = e.a, say = e.code;
        this.seq.enqueue(async () => {
          this.bubbles.value = { ...this.bubbles.value, [seat]: text };
          if (to !== seat && this.seatOk(to) && (say === Say.Accuse || say === Say.VoteFor || say === Say.Trust))
            this.flashFx({ kind: "line", seat, to, tone: say === Say.Trust ? "town" : "mafia" }, 1900);
          await wait(1900);
        });
        return;
      }
      case 5: {
        // واکنش: گذرا، بی صف و بی ترتیب — همان لحظه روی میز
        if (e.code >= REACTIONS.length || !this.seatOk(e.seat)) return;
        const to = this.seatOk(e.a) ? e.a : -1;
        this.flashFx({ kind: "react", seat: e.seat, to, n: e.code }, to >= 0 ? 2600 : 2300);
        synth("tick", { volume: 0.7 });
        return;
      }
      case 4: {
        if (!this.inOrder("sgNext", e.index) || e.replay) return;
        this.suggestions.value = { ...this.suggestions.value, [e.seat]: e.a };
        const m = this.model.value;
        if (m && e.seat !== m.me) void this.whisperFor(`${this.seatName(e.seat)} پیشنهاد داد: ${this.seatName(e.a)}`);
        return;
      }
    }
  }

  private inOrder(which: "evNext" | "spNext" | "noteNext" | "sgNext", idx: number): boolean {
    const next = this[which];
    if (idx < next) return false;
    if (idx > next) {
      if (!this.resyncAsked) { this.resyncAsked = true; client.resync(); }
      return false;
    }
    this[which] = next + 1;
    this.resyncAsked = false;
    return true;
  }

  private push(l: LogLine): void {
    this.log.value = [...this.log.value, l];
  }

  private say(text: string): void {
    if (!text) return;
    this.stage.value = { text, key: ++this.key };
  }

  private async whisperFor(text: string): Promise<void> {
    this.whisper.value = { text, key: ++this.key };
    await wait(400);
  }

  /** نمایشِ یک رویدادِ علنی (همان MafiaOnlineController.PlayEvent ِ اپ). */
  private async play(e: MfEvent): Promise<void> {
    switch (e.kind) {
      case Ev.Start: case Ev.Speech: case Ev.VoteCall:
        return;
      case Ev.Vote:
        this.ballot.push({ voter: e.a, cand: e.b });
        return;
      case Ev.VoteCount:
        await this.tally(e);
        return;
      case Ev.DayStart:
        if (e.a === 0) return;
        this.phase.value = { night: false, day: e.a };
        this.bubbles.value = {};
        this.ballot = [];
        this.say(dayTitle(e.a));
        this.flashFx({ kind: "title", text: dayTitle(e.a), night: false }, 1900);
        synth("chime", { volume: 0.7 });
        await wait(1500);
        return;
      case Ev.NightStart:
        this.phase.value = { night: true, day: e.a };
        this.bubbles.value = {};
        this.say(nightTitle(e.a));
        this.flashFx({ kind: "title", text: nightTitle(e.a), night: true }, 1900);
        synth("bell");
        await wait(1500);
        if (e.a === 0) { this.say("شب معارفه: مافیا هم را می‌بینند. امشب شلیکی نیست."); await wait(1400); }
        return;
      case Ev.Talk:
        this.bubbles.value = {};
        return;
    }
    this.eventFx(e);
    const t = eventText(e, this.seatName);
    if (!t) return;
    this.say(t);
    await wait(1700);
  }

  // ─────────────────────────── کارهای بازیکن ───────────────────────────

  private stepNo(): number { return this.model.value?.stepNo ?? 0; }

  private mark(): void {
    const m = this.model.value;
    if (m?.prompt) this.sent.value = `${m.stepNo}|${m.prompt.ask}`;
  }

  /** می‌توانم واکنش بفرستم؟ همان قاعده‌ی چت: روز و زنده؛ پس از پایان همه. */
  canReact(): boolean {
    const m = this.model.value;
    if (!m || m.watching) return false;
    return m.over || (!m.night && !m.revealing && !!m.mseats[m.me]?.alive);
  }

  /** واکنش: ایموجی بالای صندلیِ خودم (target −۱) یا رو به یک نفر؛ لایک و دیس‌لایک همیشه رو به یک نفر. */
  react(code: number, target = -1): void {
    if (!this.canReact()) return;
    if (!this.reactPace.take(reactKey(code, target))) return; // دکمه‌اش کم‌رنگ است — ReactPop
    client.mafiaAction({ kind: 7, say: code, a: target });
  }

  /** «فهمیدم» روی کارتِ نقشِ آغاز — سرور تا «فهمیدم» ِ همه گامِ اول را شروع نمی‌کند. */
  seenCard(): void {
    const m = this.model.value;
    if (m && m.revealing && !m.watching) client.mafiaAction({ kind: 6 });
  }

  /** کارتِ آغاز بسته شد (پس از انیمیشنِ رفتن به چیپ). */
  closeReveal(): void {
    this.reveal.value = false;
    this.deal.value = "";
  }

  /** جوابِ کامل به خواسته‌ی من. */
  answer(x: { a?: number; b?: number; c?: number; choice?: number; role?: number; act?: number }): void {
    const m = this.model.value;
    if (!m?.prompt || m.watching) return;
    client.mafiaAction({ kind: 1, a: x.a ?? -1, b: x.b ?? -1, c: x.c ?? -1, choice: x.choice ?? 0, role: x.role ?? 0, act: x.act ?? 0, step: this.stepNo() });
    this.mark();
  }

  /** «هیچ» برای خواسته‌ی اختیاری. */
  pass(): void {
    const m = this.model.value;
    if (!m?.prompt || m.watching) return;
    const yesNo = [Ask.Status, Ask.Vote, Ask.InterGo, Ask.DieHard, Ask.Mayor, Ask.NostraSide].includes(m.prompt.ask as never);
    if (yesNo) client.mafiaAction({ kind: 1, a: -1, b: -1, c: -1, choice: 0, step: this.stepNo() });
    else client.mafiaAction({ kind: 2, step: this.stepNo() });
    this.mark();
  }

  /** رأیِ علنی: دست بالا یا پایین — تا پایانِ گام عوض‌شدنی است. */
  setHand(up: boolean): void {
    const m = this.model.value;
    if (!m?.prompt || m.prompt.ask !== Ask.Vote || m.watching) return;
    if (!this.handPace.take("hand")) return;
    this.hand.value = up;
    client.mafiaAction({ kind: 1, a: -1, b: -1, c: -1, choice: up ? 1 : 0, step: this.stepNo() });
  }

  /** جمله در نوبتِ حرفِ من. */
  speak(kind: number, target = -1): void {
    const m = this.model.value;
    if (!m || m.watching || m.stepKind !== StepKind.Talk || m.actor !== m.me || m.saysLeft <= 0) return;
    const line = `${m.stepNo}|${kind}|${target}`;
    if (line === this.lastLine) return;
    this.lastLine = line;
    client.mafiaAction({ kind: 3, say: kind, a: target, step: this.stepNo() });
    this.model.value = { ...m, saysLeft: m.saysLeft - 1 } as MafiaState;
    if (m.saysLeft - 1 <= 0) this.endTalk();
  }

  /** تایمِ چالش: وسطِ حرفِ روزِ دیگری چالش می‌خواهم (سرور می‌سنجد که مجاز است). */
  askChallenge(): void {
    const m = this.model.value;
    if (!m?.canChallenge) return;
    client.mafiaAction({ kind: 3, say: Say.ChallengeAsk, a: -1, step: this.stepNo() });
    this.model.value = { ...m, canChallenge: false, challengers: [...m.challengers, m.me] } as MafiaState;
  }

  /** تایمِ چالش: گوینده چالش را به یکی از درخواست‌کننده‌ها می‌دهد. */
  giveChallenge(target: number): void {
    const m = this.model.value;
    if (!m || m.watching || m.stepKind !== StepKind.Talk || m.actor !== m.me || m.challengeTo >= 0 || !m.challengers.includes(target)) return;
    client.mafiaAction({ kind: 3, say: Say.ChallengeGive, a: target, step: this.stepNo() });
    this.model.value = { ...m, challengeTo: target } as MafiaState;
  }

  endTalk(): void {
    const m = this.model.value;
    if (!m || m.watching || m.stepKind !== StepKind.Talk || m.actor !== m.me) return;
    client.mafiaAction({ kind: 4, step: this.stepNo() });
    this.mark();
  }

  /** مافیای غیرِتصمیم‌گیر: هدفِ پیشنهادی به تیم. */
  suggest(target: number): void {
    const m = this.model.value;
    if (!m || m.watching || !m.night) return;
    if (!this.suggPace.take(String(target))) return;
    client.mafiaAction({ kind: 5, a: target, step: this.stepNo() });
    this.suggestions.value = { ...this.suggestions.value, [m.me]: target };
  }

  /** من بردم؟ (نما می‌گوید؛ تماشاچی هرگز.) */
  won(): boolean { return !!this.model.value?.won; }
  winnerTeam(): number { return this.model.value?.winner ?? -1; }
  isMafiaWin(): boolean { return this.winnerTeam() === Team.Mafia; }
}

/** کلیدِ سرعت‌گیرِ واکنش — همان کلیدِ سرور (table/mafia_react.go): واکنش و هدفش. */
export function reactKey(code: number, target: number): string {
  return `${code}>${target < 0 ? -1 : target}`;
}
